import React, { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from 'react-router-dom';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { Layout, Breadcrumb, Avatar, Spin, Input, Button } from 'antd';
import axios from 'axios';
import { CalendarOutlined, HomeOutlined, UserOutlined, SendOutlined, SmileOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

interface NotificationDetails {
    notificationid: string;
    notificationtype: string;
    messagetext: string;
    notificationdate: Date;
    ecardimage: string;
    nominatorname: string;
    awardname: string;
    nominatorimage: string;
    feedid?: string;
}

interface FeedDetails {
    feedId: string;
    feedType: string;
    nominatorId: string;
    nominatorName: string;
    nominatorImage?: string;
    nomineeId: string;
    nomineeName: string;
    companyValueName?: string;
    celebrationCategoryName?: string;
    message: string;
    ecardImage?: string;
    createdAt: string;
}

interface FeedComment {
    userId: string;
    displayName: string;
    commentText: string;
}

interface FeedLike {
    userId: string;
    userName: string;
    emojiCode: string;
}

const EMOJI_LIST = [
    '😀','😂','😍','😊','🥰','😎','🤩','😄','😁','😅',
    '🙌','👏','💪','👍','🙏','❤️','🔥','🎉','🎊','🌟',
    '✨','💯','😢','😮','🤔','😆','😜','🥳','😇','😏',
];

const FeedCard: React.FC<{
    feedDetails: FeedDetails;
    comments: FeedComment[];
    likes: FeedLike[];
    currentUserId: string;
    onPostComment: (text: string) => void;
    onPostLike: (emojiCode: string) => void;
    posting: boolean;
}> = ({ feedDetails, comments, likes, currentUserId, onPostComment, onPostLike, posting }) => {
    const [commentText, setCommentText] = useState('');
    const [smileyOpen, setSmileyOpen] = useState(false);
    const baseURL = process.env.REACT_APP_API_URL;

    const emojiPickerContent = (
        <div style={{ width: '240px', padding: '6px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {EMOJI_LIST.map(emoji => (
                    <span key={emoji} onClick={() => { setCommentText(t => t + emoji); setSmileyOpen(false); }}
                        style={{ fontSize: '20px', cursor: 'pointer', padding: '3px', borderRadius: '4px', lineHeight: 1 }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f5f0ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        {emoji}
                    </span>
                ))}
            </div>
        </div>
    );

    const handleSubmit = () => {
        if (!commentText.trim()) return;
        onPostComment(commentText);
        setCommentText('');
    };

    const myReaction = likes.find(l => l.userId === currentUserId)?.emojiCode;

    return (
        <div style={{ border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            {/* Feed header */}
            <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <Avatar src={feedDetails.nominatorImage ? `data:image/jpeg;base64,${feedDetails.nominatorImage}` : undefined} icon={<UserOutlined />} size={40} />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{feedDetails.nominatorName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            {feedDetails.feedType === 'Celebration' ? 'wishes' : 'appreciates'}{' '}
                            <strong>{feedDetails.nomineeName}</strong>
                            {feedDetails.companyValueName && <> for <strong style={{ color: '#8629A4' }}>{feedDetails.companyValueName}</strong></>}
                            {feedDetails.celebrationCategoryName && <> for <strong style={{ color: '#8629A4' }}>{feedDetails.celebrationCategoryName}</strong></>}
                        </div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(feedDetails.createdAt))}
                    </div>
                </div>
                {feedDetails.message && (
                    <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#444', lineHeight: 1.5 }}>{feedDetails.message}</p>
                )}
            </div>

            {/* Ecard image */}
            {feedDetails.ecardImage && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <img src={`data:image/jpeg;base64,${feedDetails.ecardImage}`} alt="eCard"
                        style={{ width: '100%', borderRadius: '8px', objectFit: 'contain' }} />
                </div>
            )}

            {/* Emoji reactions */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {['👍','❤️','🎉','🔥','🙌'].map(emoji => {
                    const count = likes.filter(l => l.emojiCode === emoji).length;
                    const active = myReaction === emoji;
                    return (
                        <button key={emoji} onClick={() => onPostLike(emoji)}
                            style={{ background: active ? '#f5f0ff' : '#f5f5f5', border: active ? '1px solid #8629A4' : '1px solid #e8e8e8',
                                borderRadius: '16px', padding: '3px 10px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {emoji}{count > 0 && <span style={{ fontSize: '12px', color: '#666' }}>{count}</span>}
                        </button>
                    );
                })}
                {likes.length > 0 && (
                    <span style={{ fontSize: '12px', color: '#888', marginLeft: 'auto' }}>
                        {likes.length} reaction{likes.length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Comments list */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                {comments.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: '13px', margin: 0 }}>No comments yet. Be the first!</p>
                ) : (
                    comments.map((c, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', background: '#f9f9f9', borderRadius: '8px', padding: '8px 12px' }}>
                            <Avatar icon={<UserOutlined />} size={32} />
                            <div>
                                <strong style={{ fontSize: '13px' }}>{c.displayName}</strong>
                                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#555', wordBreak: 'break-word' }}>
                                    {c.commentText.split(/(@\[[^\]]+\])/g).map((part, j) =>
                                        /^@\[.+\]$/.test(part)
                                            ? <span key={j} style={{ color: '#8629A4', fontWeight: 600 }}>@{part.slice(2, -1)}</span>
                                            : part
                                    )}
                                </p>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Comment input */}
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar icon={<UserOutlined />} size={32} />
                <div style={{ flex: 1 }}>
                    <Input
                        placeholder="Write a comment..."
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        onPressEnter={handleSubmit}
                        disabled={posting}
                        style={{ borderRadius: '20px', backgroundColor: '#f0f0f0', border: 'none' }}
                        prefix={
                            <Popover content={emojiPickerContent} trigger="click" open={smileyOpen} onOpenChange={setSmileyOpen} placement="topLeft">
                                <SmileOutlined style={{ color: '#8629A4', cursor: 'pointer', fontSize: '16px' }} />
                            </Popover>
                        }
                        suffix={
                            <Button type="text" icon={<SendOutlined />} onClick={handleSubmit} loading={posting}
                                disabled={posting || !commentText.trim()}
                                style={{ color: '#1890ff', border: 'none', boxShadow: 'none', padding: '0 0 0 8px' }} />
                        }
                    />
                </div>
            </div>
        </div>
    );
};

const ViewNotification: React.FC = () => {
    const location = useLocation();
    const { notificationId, notificationType } = location.state || {};
    const { instance, accounts } = useMsal();
    const [notificationData, setNotificationData] = useState<NotificationDetails[]>([]);
    const [feedData, setFeedData] = useState<{ [notifId: string]: { details: FeedDetails; comments: FeedComment[]; likes: FeedLike[]; feedId?: string } }>({});
    const [loading, setLoading] = useState(true);
    const [feedLoading, setFeedLoading] = useState<Set<string>>(new Set());
    const [posting, setPosting] = useState<{ [notifId: string]: boolean }>({});

    const baseURL = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("UserId") || '';

    const fetchToken = useCallback(async () => {
        const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return res.idToken;
    }, [instance, accounts]);

    const fetchFeed = useCallback(async (notifId: string, feedId: string, token: string) => {
        setFeedLoading(prev => new Set(prev).add(notifId));
        try {
            const res = await axios.get(`${baseURL}/api/Feeds/GetFeedById/${feedId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setFeedData(prev => ({ ...prev, [notifId]: { details: res.data.details, comments: res.data.comments, likes: res.data.likes, feedId } }));
            }
        } catch { /* non-fatal */ }
        finally { setFeedLoading(prev => { const s = new Set(prev); s.delete(notifId); return s; }); }
    }, [baseURL]);

    const fetchFeedBySourceId = useCallback(async (notifId: string, type: string, token: string) => {
        setFeedLoading(prev => new Set(prev).add(notifId));
        try {
            const res = await axios.get(`${baseURL}/api/Feeds/GetFeedBySourceId`, {
                params: { sourceId: notifId, type },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                const resolvedFeedId: string | undefined = res.data.details?.feedId;
                setFeedData(prev => ({ ...prev, [notifId]: { details: res.data.details, comments: res.data.comments, likes: res.data.likes, feedId: resolvedFeedId } }));
            }
        } catch { /* no feed for this notification */ }
        finally { setFeedLoading(prev => { const s = new Set(prev); s.delete(notifId); return s; }); }
    }, [baseURL]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const token = await fetchToken();
                let data: NotificationDetails[] = [];

                if (notificationType !== "All") {
                    const res = await axios.get(`${baseURL}/api/UserNotification/GetUserNotificationsById`, {
                        params: { notification_id: notificationId, notification_type: notificationType },
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.status === 200) data = [...res.data];
                } else {
                    const res = await axios.get(`${baseURL}/api/UserNotification/GetAllUserNotifications/${userId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.status === 200) data = [...res.data];
                }

                data.sort((a, b) => new Date(b.notificationdate).getTime() - new Date(a.notificationdate).getTime());
                setNotificationData(data);

                // Fetch full feed for each notification that has a feed card
                for (const n of data) {
                    if (n.notificationtype === 'Mention' && n.feedid) {
                        fetchFeed(n.notificationid, n.feedid, token);
                    } else if (['Appreciation', 'Celebration', 'Nomination'].includes(n.notificationtype)) {
                        fetchFeedBySourceId(n.notificationid, n.notificationtype, token);
                    }
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
            } finally {
                setLoading(false);
            }
        })();
    }, [notificationId, notificationType]);

    const handlePostComment = useCallback(async (notifId: string, text: string) => {
        const feedId = feedData[notifId]?.feedId;
        if (!feedId) return;
        setPosting(prev => ({ ...prev, [notifId]: true }));
        try {
            const token = await fetchToken();
            await axios.post(`${baseURL}/api/Feeds/Comments`, {
                userId, feedId, interactionType: 'COMMENT', commentText: text, mentionedUserIds: []
            }, { headers: { Authorization: `Bearer ${token}` } });
            await fetchFeed(notifId, feedId, token);
        } catch { /* non-fatal */ } finally {
            setPosting(prev => ({ ...prev, [notifId]: false }));
        }
    }, [baseURL, fetchToken, userId, fetchFeed, feedData]);

    const handlePostLike = useCallback(async (notifId: string, emojiCode: string) => {
        const feedId = feedData[notifId]?.feedId;
        if (!feedId) return;
        try {
            const token = await fetchToken();
            await axios.post(`${baseURL}/api/Feeds/Likes`, {
                userId, feedId, interactionType: 'EMOJI_REACTION', emojiCode
            }, { headers: { Authorization: `Bearer ${token}` } });
            await fetchFeed(notifId, feedId, token);
        } catch { /* non-fatal */ }
    }, [baseURL, fetchToken, userId, fetchFeed, feedData]);

    return (
        <Layout style={{ background: '#F9FAFB' }}>
            <AppHeaderHomePageTemplate />
            <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0px', minHeight: '100vh', width: '100%' }}>
                <Breadcrumb style={{ backgroundColor: 'white', padding: '20px 20px 10px' }}>
                    <Breadcrumb.Item>
                        <Link to="/home"><HomeOutlined style={{ marginRight: '5px' }} />Home</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Notifications</Breadcrumb.Item>
                </Breadcrumb>

                <div style={{ backgroundColor: 'white', padding: '20px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <Spin size="large" tip="Loading notifications..." />
                        </div>
                    ) : notificationData.length > 0 ? (
                        notificationData.map(item => (
                            <div key={item.notificationid} style={{ marginBottom: '24px', border: '1px solid #E0E0E0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#F9FAFB' }}>
                                {/* Notification header */}
                                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Avatar src={item.nominatorimage ? `data:image/jpeg;base64,${item.nominatorimage}` : undefined} size={40} icon={<UserOutlined />} />
                                    <div style={{ flex: 1 }}>
                                        <span style={{ fontWeight: 400, fontSize: '15px', color: '#000' }}>
                                            {item.notificationtype === "Appreciation" && <><strong>{item.nominatorname}</strong> appreciates you for demonstrating <strong>{item.awardname}</strong>.</>}
                                            {item.notificationtype === "Celebration" && <><strong>{item.nominatorname}</strong> wishes you for <strong>{item.awardname}</strong>.</>}
                                            {item.notificationtype === "Nomination" && <>You have received <strong>{item.awardname}</strong> from <strong>{item.nominatorname}</strong>.</>}
                                            {item.notificationtype === "Points" && <>You have been awarded <strong>{item.awardname}</strong>{item.nominatorname ? <> by <strong>{item.nominatorname}</strong></> : ''}.</>}
                                            {item.notificationtype === "Mention" && <><strong>{item.nominatorname}</strong> mentioned you in a comment.</>}
                                        </span>
                                        <div style={{ fontSize: '12px', color: '#888888', marginTop: '3px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <CalendarOutlined />
                                            {new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(item.notificationdate))}
                                        </div>
                                    </div>
                                </div>

                                {/* Feed card for types that have an associated feed */}
                                {['Appreciation', 'Celebration', 'Nomination', 'Mention'].includes(item.notificationtype) && (
                                    <div style={{ padding: '14px 16px' }}>
                                        {feedLoading.has(item.notificationid) ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}><Spin size="small" /></div>
                                        ) : feedData[item.notificationid]?.details ? (
                                            <FeedCard
                                                feedDetails={feedData[item.notificationid].details}
                                                comments={feedData[item.notificationid].comments}
                                                likes={feedData[item.notificationid].likes}
                                                currentUserId={userId}
                                                posting={posting[item.notificationid] || false}
                                                onPostComment={(text) => handlePostComment(item.notificationid, text)}
                                                onPostLike={(emoji) => handlePostLike(item.notificationid, emoji)}
                                            />
                                        ) : (
                                            /* Fallback: show message + ecard if no feed row found */
                                            <div>
                                                {item.messagetext && <p style={{ fontSize: '14px', color: '#444', margin: '0 0 10px' }}>{item.messagetext}</p>}
                                                {item.ecardimage && (
                                                    <img src={`data:image/jpeg;base64,${item.ecardimage}`} alt="eCard"
                                                        style={{ width: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p style={{ fontSize: '16px', color: '#888888' }}>No notifications available.</p>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ViewNotification;
